import { useState, useEffect, useRef } from 'react';
import { Settings, X } from 'lucide-react';
import { logger } from '../../lib/terminalLogger';
import { corelCommunication } from '../../lib/corel/communication';
import { useNotifications } from '../feedback/NotificationProvider';
import { notifyToolsError } from '../../lib/toolsErrorNotification';
import { api } from '../../lib/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { storage } from '../../lib/storage/storage';
import { Keys } from '../../lib/storage/keys';

type Unit = 'mm' | 'cm';

interface CmykColor {
  c: number;
  m: number;
  y: number;
  k: number;
}

/**
 * Lista de materiais padrão (fallback caso API não retorne dados)
 */
interface OneReguaColorPrefs {
  line: string;
  text: string;
  outline: string;
}

const DEFAULT_LINE_COLOR: CmykColor = { c: 100, m: 20, y: 0, k: 0 };
const DEFAULT_TEXT_COLOR: CmykColor = { c: 0, m: 0, y: 0, k: 100 };
const DEFAULT_OUTLINE_COLOR: CmykColor = { c: 0, m: 0, y: 0, k: 0 };

const FALLBACK_MATERIALS = [
  'DTF TÊXTIL',
  'SUBLIMAÇÃO',
  'DTF UV',
  'ADESIVO UV',
];

const FONT_OPTIONS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Verdana',
  'Calibri',
  'Tahoma',
  'Trebuchet MS',
  'Georgia',
  'Courier New',
];

const LINE_STYLE_OPTIONS = [
  { value: 0, label: 'Sólido' },
  { value: 8, label: 'Tracejado' },
];

const ARROW_HEAD_OPTIONS = [
  { value: 53, label: 'Bola' },
  { value: 59, label: 'Linha' },
  { value: 0, label: 'Nenhum' },
];

/**
 * Hook para buscar materiais da API /cdr/rules/
 * Extrai nomes únicos dos produtos e usa fallback se falhar
 */
function useMaterials() {
  const [materials, setMaterials] = useState<string[]>(FALLBACK_MATERIALS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchMaterials() {
      try {
        const rules = await api('/cdr/rules/', { method: 'GET' });

        if (Array.isArray(rules) && rules.length > 0) {
          const uniqueNames = new Set<string>();
          rules.forEach((rule: any) => {
            const nome = rule.produto?.nome_servico;
            if (nome && typeof nome === 'string') {
              uniqueNames.add(nome.toUpperCase());
            }
          });

          if (uniqueNames.size > 0) {
            const sortedMaterials = Array.from(uniqueNames).sort();
            setMaterials(sortedMaterials);
            logger.debug('[OneRegua] Materiais carregados da API:', sortedMaterials.length);
          }
        }
      } catch (error) {
        logger.debug('[OneRegua] Erro ao buscar materiais, usando fallback:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMaterials();
  }, []);

  return { materials, isLoading };
}

function parseScale(scale: string | number): number {
  if (typeof scale === 'number') return scale;
  if (typeof scale === 'string') {
    const match = scale.match(/^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/);
    if (match) {
      const numerator = parseFloat(match[1]);
      const denominator = parseFloat(match[2]);
      if (denominator !== 0) return numerator / denominator;
    }
    const parsed = parseFloat(scale);
    if (!isNaN(parsed)) return parsed;
  }
  return 1.0;
}

/**
 * Converte hex (#RRGGBB) para CMYK aproximado (0-100)
 */
function hexToCmyk(hex: string): CmykColor {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const kVal = 1 - Math.max(r, g, b);
  if (kVal === 1) return { c: 0, m: 0, y: 0, k: 100 };
  return {
    c: Math.round(((1 - r - kVal) / (1 - kVal)) * 100),
    m: Math.round(((1 - g - kVal) / (1 - kVal)) * 100),
    y: Math.round(((1 - b - kVal) / (1 - kVal)) * 100),
    k: Math.round(kVal * 100),
  };
}

/**
 * Converte CMYK (0-100) para hex (#RRGGBB) aproximado
 */
function cmykToHex(cmyk: CmykColor): string {
  const { c, m, y, k } = cmyk;
  const r = Math.round(255 * (1 - c / 100) * (1 - k / 100));
  const g = Math.round(255 * (1 - m / 100) * (1 - k / 100));
  const b = Math.round(255 * (1 - y / 100) * (1 - k / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

function isOneReguaColorPrefs(value: unknown): value is OneReguaColorPrefs {
  if (!value || typeof value !== 'object') return false;
  const prefs = value as Partial<OneReguaColorPrefs>;
  return isHexColor(prefs.line) && isHexColor(prefs.text) && isHexColor(prefs.outline);
}

function ColorSelector({
  label,
  cmyk,
  onChange,
}: {
  label: string;
  cmyk: CmykColor;
  onChange: (cmyk: CmykColor) => void;
}) {
  const hexValue = cmykToHex(cmyk);

  return (
    <label className="flex items-center justify-between gap-3 rounded-md border border-navy-700 bg-navy-900/50 px-2.5 py-2 text-xs text-gray-300">
      <span>{label}</span>
      <input
        type="color"
        value={hexValue}
        onChange={(e) => onChange(hexToCmyk(e.target.value))}
        className="h-7 w-10 cursor-pointer rounded border border-navy-600 bg-navy-900 p-0"
      />
    </label>
  );
}

/**
 * Modal de Configurações Avançadas
 */
interface AdvancedSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  distanceMm: string;
  setDistanceMm: (v: string) => void;
  lineThicknessMm: string;
  setLineThicknessMm: (v: string) => void;
  scale: string;
  setScale: (v: string) => void;
  includeUnitSuffix: boolean;
  setIncludeUnitSuffix: (v: boolean) => void;
  simpleMode: boolean;
  setSimpleMode: (v: boolean) => void;
  fontName: string;
  setFontName: (v: string) => void;
  textOutlineEnabled: boolean;
  setTextOutlineEnabled: (v: boolean) => void;
  outlineWidthPt: string;
  setOutlineWidthPt: (v: string) => void;
  lineStyleIndex: number;
  setLineStyleIndex: (v: number) => void;
  arrowHeadIndex: number;
  setArrowHeadIndex: (v: number) => void;
}

function AdvancedSettingsModal({
  isOpen, onClose,
  distanceMm, setDistanceMm,
  lineThicknessMm, setLineThicknessMm,
  scale, setScale,
  includeUnitSuffix, setIncludeUnitSuffix,
  simpleMode, setSimpleMode,
  fontName, setFontName,
  textOutlineEnabled, setTextOutlineEnabled,
  outlineWidthPt, setOutlineWidthPt,
  lineStyleIndex, setLineStyleIndex,
  arrowHeadIndex, setArrowHeadIndex,
}: AdvancedSettingsModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-navy-800 border border-navy-700 rounded-lg w-full max-w-[360px] shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-navy-700">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-white">Configurações Avançadas</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-navy-700 rounded transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 space-y-3">
          {/* Espaçamento e Espessura */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Espaçamento (mm)</label>
              <input
                value={distanceMm}
                onChange={(e) => setDistanceMm(e.target.value)}
                placeholder="5"
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Espessura (mm)</label>
              <input
                value={lineThicknessMm}
                onChange={(e) => setLineThicknessMm(e.target.value)}
                placeholder="0.5"
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Escala e Fonte */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Escala</label>
              <input
                value={scale}
                onChange={(e) => setScale(e.target.value)}
                placeholder="1/1"
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Fonte</label>
              <select
                value={fontName}
                onChange={(e) => setFontName(e.target.value)}
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              >
                {FONT_OPTIONS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contorno do texto */}
          <div className="space-y-2 pt-1 border-t border-navy-700">
            <span className="block text-xs text-gray-400 font-medium pt-1">Contorno do texto (halo)</span>
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={textOutlineEnabled}
                onChange={(e) => setTextOutlineEnabled(e.target.checked)}
                className="rounded border-navy-600 bg-navy-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              Ativar contorno (halo)
            </label>
            {textOutlineEnabled && (
              <>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Espessura do contorno (pt, 0 = auto)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={outlineWidthPt}
                    onChange={(e) => setOutlineWidthPt(e.target.value)}
                    placeholder="0"
                    className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </>
            )}
          </div>

          {/* Estilo de linha */}
          <div className="space-y-2 pt-1 border-t border-navy-700">
            <span className="block text-xs text-gray-400 font-medium pt-1">Estilo de linha</span>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Tipo de linha</label>
                <select
                  value={lineStyleIndex}
                  onChange={(e) => setLineStyleIndex(parseInt(e.target.value))}
                  className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                >
                  {LINE_STYLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Ponta da seta</label>
                <select
                  value={arrowHeadIndex}
                  onChange={(e) => setArrowHeadIndex(parseInt(e.target.value))}
                  className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                >
                  {ARROW_HEAD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-2 pt-1 border-t border-navy-700">
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includeUnitSuffix}
                onChange={(e) => setIncludeUnitSuffix(e.target.checked)}
                className="rounded border-navy-600 bg-navy-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              Incluir sufixo de unidade (cm/mm)
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={simpleMode}
                onChange={(e) => setSimpleMode(e.target.checked)}
                className="rounded border-navy-600 bg-navy-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              Modo simples (posição fixa inferior)
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-navy-700">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium py-2 px-4 rounded transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

export function OneReguaPage() {
  const { notify } = useNotifications();
  const { role, userInfo } = usePermissions();
  const { materials, isLoading: isLoadingMaterials } = useMaterials();
  const userId = (userInfo as any)?.id;
  const colorPrefsKey = Keys.prefs.oneregua.colors(role, userId);
  const colorPrefsReadyRef = useRef(false);
  const [colorPrefsReady, setColorPrefsReady] = useState(false);

  // Estados principais (visíveis)
  const [unit, setUnit] = useState<Unit>('cm');
  const [material, setMaterial] = useState<string>('');
  const [quantitativo, setQuantitativo] = useState<string>('');
  const [multiplyOnCreate, setMultiplyOnCreate] = useState<boolean>(false);
  const [position, setPosition] = useState<number>(4);

  // Estados avançados (modal)
  const [distanceMm, setDistanceMm] = useState<string>('5');
  const [lineThicknessMm, setLineThicknessMm] = useState<string>('0.5');
  const [textSizePt, setTextSizePt] = useState<string>('40');
  const [scale, setScale] = useState<string>('1/1');
  const [includeUnitSuffix, setIncludeUnitSuffix] = useState<boolean>(true);
  const [simpleMode, setSimpleMode] = useState<boolean>(false);

  // Novos estados: aparência
  const [fontName, setFontName] = useState<string>('Arial');
  const [textOutlineEnabled, setTextOutlineEnabled] = useState<boolean>(true);
  const [lineColor, setLineColor] = useState<CmykColor>(DEFAULT_LINE_COLOR);
  const [textColor, setTextColor] = useState<CmykColor>(DEFAULT_TEXT_COLOR);
  const [materialTextColor, setMaterialTextColor] = useState<CmykColor>({ c: 0, m: 60, y: 0, k: 0 });
  const [outlineColor, setOutlineColor] = useState<CmykColor>(DEFAULT_OUTLINE_COLOR);
  const [outlineWidthPt, setOutlineWidthPt] = useState<string>('0');
  const [lineStyleIndex, setLineStyleIndex] = useState<number>(8);
  const [arrowHeadIndex, setArrowHeadIndex] = useState<number>(53);

  // Modal state
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    colorPrefsReadyRef.current = false;
    setColorPrefsReady(false);

    try {
      const saved = storage.get<OneReguaColorPrefs>(colorPrefsKey, {
        area: 'local',
        validate: isOneReguaColorPrefs,
      });

      if (saved) {
        setLineColor(hexToCmyk(saved.line));
        setTextColor(hexToCmyk(saved.text));
        setOutlineColor(hexToCmyk(saved.outline));
      } else {
        setLineColor(DEFAULT_LINE_COLOR);
        setTextColor(DEFAULT_TEXT_COLOR);
        setOutlineColor(DEFAULT_OUTLINE_COLOR);
      }
    } catch {
      // Mantem as cores padrao se o storage estiver indisponivel.
    } finally {
      colorPrefsReadyRef.current = true;
      setColorPrefsReady(true);
    }
  }, [colorPrefsKey]);

  useEffect(() => {
    if (!colorPrefsReady || !colorPrefsReadyRef.current) return;

    try {
      storage.set<OneReguaColorPrefs>(
        colorPrefsKey,
        {
          line: cmykToHex(lineColor),
          text: cmykToHex(textColor),
          outline: cmykToHex(outlineColor),
        },
        { area: 'local' }
      );
    } catch {
      // Preferencia visual: falha de storage nao deve bloquear a ferramenta.
    }
  }, [colorPrefsReady, colorPrefsKey, lineColor, textColor, outlineColor]);

  // Listener para Enter key - aplica quando pressionado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorar se modal avançado estiver aberto
      if (showAdvanced) return;

      // Ignorar se estiver em um campo de texto ou textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;

      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAdvanced, distanceMm, lineThicknessMm, textSizePt, scale, position, unit, includeUnitSuffix, simpleMode, material, quantitativo, multiplyOnCreate, fontName, textOutlineEnabled, lineColor, textColor, materialTextColor, outlineColor, outlineWidthPt, lineStyleIndex, arrowHeadIndex]);

  const getPositionTooltip = (pos: number) => {
    switch (pos) {
      case 1: return 'Inferior direita';
      case 2: return 'Superior direita';
      case 3: return 'Superior';
      case 4: return 'Esquerda';
      case 5: return 'Interna esquerda';
      case 6: return 'Interna central';
      case 7: return 'Interna direita';
      case 8: return 'Inferior';
      default: return 'Inferior direita';
    }
  };

  const getAffectedBlocks = (pos: number) => {
    switch (pos) {
      case 1: return [4, 8, 7, 2];
      case 2: return [6, 3, 1, 7];
      case 3: return [2, 6, 4, 5];
      case 4: return [3, 8, 5, 1];
      case 5: return [4, 3];
      case 6: return [3, 2];
      case 7: return [2, 1];
      case 8: return [1, 4];
      default: return [];
    }
  };

  const affectedBlocks = getAffectedBlocks(position);

  const validateFields = () => {
    const errors: string[] = [];
    const distance = parseFloat(distanceMm);
    if (isNaN(distance) || distance < 0.1) errors.push('Espaçamento deve ser ≥ 0.1mm');
    const thickness = parseFloat(lineThicknessMm);
    if (isNaN(thickness) || thickness < 0.1) errors.push('Espessura da linha deve ser ≥ 0.1mm');
    const textSize = parseFloat(textSizePt);
    if (isNaN(textSize) || textSize < 1.0) errors.push('Tamanho do texto deve ser ≥ 1.0pt');
    if (!['mm', 'cm'].includes(unit)) errors.push('Unidade deve ser mm ou cm');
    if (!simpleMode && (position < 1 || position > 8)) errors.push('Posição deve estar entre 1 e 8');
    const scaleRegex = /^\d+\/\d+$/;
    if (scale && !scaleRegex.test(scale)) errors.push('Escala deve estar no formato "n/d" (ex: 1/5)');
    if (quantitativo && quantitativo.trim() !== '') {
      const quantityValue = parseInt(quantitativo);
      if (isNaN(quantityValue) || quantityValue < 1) errors.push('Quantitativo deve ser maior que 0');
    }
    return errors;
  };

  const handleApply = async () => {
    const errors = validateFields();
    if (errors.length > 0) {
      notifyToolsError(new Error('Erros de validação:\n' + errors.join('\n')), notify, 'tool');
      return;
    }

    const data: Record<string, any> = {
      dimensionDistance: parseFloat(distanceMm),
      dimensionLineThickness: parseFloat(lineThicknessMm),
      dimensionTextSize: parseFloat(textSizePt),
      scale: parseScale(scale || '1/1'),
      position: simpleMode ? 8 : position,
      unit,
      includeUnitSuffix,
      simpleMode,
      targetLayer: 'OneRegua',
      addCornerTicks: true,
      technicalDrawing: true,
      fontName,
      textOutlineEnabled,
      dimensionLineColor: lineColor,
      textColor,
      outlineColor,
      outlineWidthPt: parseFloat(outlineWidthPt) || 0,
      lineStyleIndex,
      arrowHeadIndex,
    };

    if (material && material.trim() !== '') {
      data.material = material.trim();
      data.materialTextColor = materialTextColor;
    }

    if (quantitativo && quantitativo.trim() !== '') {
      const quantityValue = parseInt(quantitativo);
      if (!isNaN(quantityValue) && quantityValue > 0) {
        data.quantity = quantityValue;
        if (multiplyOnCreate) data.multiplyOnCreate = true;
      }
    }

    try {
      logger.debug('Aplicando OneRégua (auto-dimensions):', data);

      const result = await new Promise<any>((resolve) => {
        const id = corelCommunication.sendActionGuarded('auto-dimensions', data, (resp: any) => {
          resolve(resp);
        });
        if (!id) resolve({ status: 'erro', code: 'sessao_indisponivel', message: 'Sessão indisponível' });
      });

      if (result.status === 'ok') {
        const resultData = result.result || {};
        const { objectsMultiplied } = resultData;
        const hasMultiplied = objectsMultiplied !== undefined && objectsMultiplied !== null && objectsMultiplied > 0;

        if (hasMultiplied || (multiplyOnCreate && data.quantity && data.quantity > 0)) {
          const copiesCount = hasMultiplied ? objectsMultiplied : (data.quantity - 1);
          if (copiesCount > 0) {
            notify({
              type: 'success',
              title: 'Objetos multiplicados',
              message: `${copiesCount} ${copiesCount === 1 ? 'cópia criada' : 'cópias criadas'} com sucesso`,
              duration: 4000
            });
          }
        }
      } else {
        const errorMessage = result.message || 'Erro ao aplicar régua. Verifique se há objetos selecionados.';
        notifyToolsError(new Error(errorMessage), notify, 'tool');
      }
    } catch (error) {
      logger.error('Erro ao aplicar régua:', error);
      notifyToolsError(error, notify, 'tool');
    }
  };

  const handleClear = async () => {
    try {
      const result = await new Promise<any>((resolve) => {
        const id = corelCommunication.sendActionGuarded('clear-paste-layer', { targetLayer: 'OneRegua' }, (resp: any) => {
          resolve(resp);
        });
        if (!id) resolve({ status: 'error', message: 'Sessão indisponível' });
      });

      if (result.status !== 'ok') {
        const errorMessage = result.message || 'Erro ao limpar réguas.';
        notifyToolsError(new Error(errorMessage), notify, 'tool');
      }
    } catch (error) {
      notifyToolsError(new Error('Erro ao limpar réguas. Verifique se há um documento ativo.'), notify, 'tool');
    }
  };

  const materialColorHex = cmykToHex(materialTextColor);

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3 pb-14 space-y-3">

        {/* Seção: Material e Unidade */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-300 font-medium">Configuração</span>
            <button
              onClick={() => setShowAdvanced(true)}
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 hover:text-white bg-navy-700/50 hover:bg-navy-700 rounded transition-colors"
            >
              <Settings className="w-3.5 h-3.5" />
              Avançado
            </button>
          </div>

          {/* Material */}
          <div className="mb-3">
            <label className="block text-xs text-gray-400 mb-1">Material</label>
            <select
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              disabled={isLoadingMaterials}
              className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 disabled:opacity-50 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Sem material</option>
              {materials.map((mat) => (
                <option key={mat} value={mat}>{mat}</option>
              ))}
            </select>
            {material && (
              <p className="text-xs mt-1.5 flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ backgroundColor: materialColorHex }}
                  title={`Cor CMYK: ${materialTextColor.c}, ${materialTextColor.m}, ${materialTextColor.y}, ${materialTextColor.k}`}
                />
                <span className="text-gray-400">"{material}" acima do objeto</span>
              </p>
            )}
          </div>

          {/* Unidade, Tamanho Fonte, Quantitativo */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Unidade</label>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value as Unit)}
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              >
                <option value="cm">cm</option>
                <option value="mm">mm</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Fonte (pt)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={textSizePt}
                onChange={(e) => setTextSizePt(e.target.value)}
                placeholder="40"
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Qtd</label>
              <input
                type="number"
                min="0"
                step="1"
                value={quantitativo}
                onChange={(e) => {
                  setQuantitativo(e.target.value);
                  if (!e.target.value || parseInt(e.target.value) <= 0) {
                    setMultiplyOnCreate(false);
                  }
                }}
                placeholder="12"
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Multiplicar checkbox */}
          {quantitativo && parseInt(quantitativo) > 0 && (
            <label className="flex items-center gap-2 mt-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={multiplyOnCreate}
                onChange={(e) => setMultiplyOnCreate(e.target.checked)}
                className="rounded border-navy-600 bg-navy-900 text-blue-500"
              />
              Multiplicar objetos ({parseInt(quantitativo) - 1} cópias)
            </label>
          )}
        </section>

        {/* Seção: Cores */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3">
          <div className="text-sm text-gray-300 font-medium mb-3">Cores</div>
          <div className="grid grid-cols-3 gap-2">
            <ColorSelector label="Linha" cmyk={lineColor} onChange={setLineColor} />
            <ColorSelector label="Texto" cmyk={textColor} onChange={setTextColor} />
            <ColorSelector label="Contorno" cmyk={outlineColor} onChange={setOutlineColor} />
          </div>
        </section>

        {/* Seção: Posição */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3">
          <div className="text-sm text-gray-300 font-medium mb-2">
            {simpleMode ? 'Posição (Modo Simples)' : 'Posição das Dimensões'}
          </div>

          <div className="flex justify-center">
            <div className="grid grid-cols-3 gap-1.5">
              {[3, 6, 2, 5, 0, 7, 4, 8, 1].map((pos, idx) => {
                const isAffected = !simpleMode && affectedBlocks.includes(pos);
                const isSelected = !simpleMode && pos === position;
                const isSimpleSelected = simpleMode && pos === 8;

                return (
                  <button
                    key={idx}
                    onClick={() => !simpleMode && pos !== 0 && setPosition(pos)}
                    className={`w-10 h-10 flex items-center justify-center border rounded text-sm font-medium transition-all ${
                      isSimpleSelected || isSelected
                        ? 'bg-blue-600 border-blue-400 text-white shadow-md'
                        : pos === 0
                        ? 'bg-navy-700 border-navy-600 cursor-default'
                        : isAffected
                        ? 'bg-emerald-600/30 border-emerald-400 text-emerald-200'
                        : simpleMode
                        ? 'bg-navy-700 border-navy-600 text-gray-500 cursor-not-allowed'
                        : 'bg-navy-700 border-navy-600 text-gray-300 hover:bg-navy-600 hover:border-navy-500'
                    }`}
                    disabled={pos === 0 || simpleMode}
                  >
                    {pos !== 0 ? pos : <div className="w-3 h-3 bg-gray-500 rounded-sm" />}
                  </button>
                );
              })}
            </div>
          </div>

          {(position > 0 || simpleMode) && (
            <p className="mt-2 text-center text-xs text-gray-400">
              {simpleMode ? 'Posição 8 - Inferior' : `Posição ${position} - ${getPositionTooltip(position)}`}
            </p>
          )}
        </section>

        {/* Botões de Ação */}
        <div className="grid grid-cols-2 gap-2">
          <button
            className="bg-navy-700 hover:bg-navy-600 text-gray-300 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            onClick={handleClear}
          >
            Limpar
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            onClick={handleApply}
          >
            Aplicar
          </button>
        </div>
      </div>

      {/* Modal de Configurações Avançadas */}
      <AdvancedSettingsModal
        isOpen={showAdvanced}
        onClose={() => setShowAdvanced(false)}
        distanceMm={distanceMm}
        setDistanceMm={setDistanceMm}
        lineThicknessMm={lineThicknessMm}
        setLineThicknessMm={setLineThicknessMm}
        scale={scale}
        setScale={setScale}
        includeUnitSuffix={includeUnitSuffix}
        setIncludeUnitSuffix={setIncludeUnitSuffix}
        simpleMode={simpleMode}
        setSimpleMode={setSimpleMode}
        fontName={fontName}
        setFontName={setFontName}
        textOutlineEnabled={textOutlineEnabled}
        setTextOutlineEnabled={setTextOutlineEnabled}
        outlineWidthPt={outlineWidthPt}
        setOutlineWidthPt={setOutlineWidthPt}
        lineStyleIndex={lineStyleIndex}
        setLineStyleIndex={setLineStyleIndex}
        arrowHeadIndex={arrowHeadIndex}
        setArrowHeadIndex={setArrowHeadIndex}
      />
    </div>
  );
}
